import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { CreateDropdown } from '../components/CreateDropdown';
import { ValidateRole } from '../helpers/ValidateRole';
import { NewProjectForm } from '../components/NewProjectForm';
import { NotificationButton } from '../components/NotificationButton';
import { getDocuments, downloadDocument, deleteDocument } from '../api/documents';
import { getProjects } from '../api/projects';
import { API_URL } from '../config/api';

interface DocumentInterface {
  id: string;
  name: string;
  size: number;
  mimetype?: string;
  uploadedBy: string;
  uploadedAt: string;
  projectId?: string;
  url?: string;
  version?: string;
  owner?: string;
}

interface ProjectInterface {
  id: string;
  name: string;
}

export const Documents = () => {
  const { keycloak: { token } } = useKeycloak();
  const navigate = useNavigate();
  const [showNewProject, setShowNewProject] = useState<boolean>(false);
  const [documents, setDocuments] = useState<DocumentInterface[]>([]);
  const [projects, setProjects] = useState<ProjectInterface[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      // Load all documents and projects in parallel
      const [documentsResponse, projectsResponse] = await Promise.all([
        getDocuments(token), // Get all documents without project filter
        getProjects(token)
      ]);
      
      console.log('All documents loaded:', documentsResponse);
      console.log('All projects loaded:', projectsResponse);
      
      setDocuments(documentsResponse.results || []);
      setProjects(projectsResponse.results || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const getProjectName = (projectId?: string): string => {
    if (!projectId) return 'No Project';
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Unknown';
    }
  };

  const getFileType = (mimetype?: string, filename?: string): string => {
    if (!mimetype && !filename) return 'Unknown';
    
    if (!mimetype && filename) {
      const ext = filename.split('.').pop()?.toLowerCase();
      switch (ext) {
        case 'pdf': return 'PDF';
        case 'doc': case 'docx': return 'Word Document';
        case 'xls': case 'xlsx': return 'Excel Spreadsheet';
        case 'ppt': case 'pptx': return 'PowerPoint';
        case 'jpg': case 'jpeg': case 'png': case 'gif': case 'bmp': return 'Image';
        case 'mp4': case 'avi': case 'mov': case 'wmv': return 'Video';
        case 'mp3': case 'wav': case 'flac': return 'Audio';
        case 'txt': return 'Text';
        case 'zip': case 'rar': case '7z': return 'Archive';
        default: return ext ? ext.toUpperCase() : 'Unknown';
      }
    }
    
    if (mimetype) {
      if (mimetype.includes('pdf')) return 'PDF';
      if (mimetype.includes('word') || mimetype.includes('document')) return 'Word Document';
      if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) return 'Excel Spreadsheet';
      if (mimetype.includes('powerpoint') || mimetype.includes('presentation')) return 'PowerPoint';
      if (mimetype.includes('image')) return 'Image';
      if (mimetype.includes('video')) return 'Video';
      if (mimetype.includes('audio')) return 'Audio';
      if (mimetype.includes('text')) return 'Text';
      if (mimetype.includes('zip') || mimetype.includes('archive')) return 'Archive';
      
      const parts = mimetype.split('/');
      if (parts.length > 1) {
        return parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      }
    }
    
    return 'Unknown';
  };

  const handleDocumentClick = async (doc: DocumentInterface) => {
    if (!token) return;

    try {
      console.log('Opening document:', doc.id, doc.name);
      
      if (doc.url) {
        let documentUrl = doc.url;
        if (documentUrl.startsWith('https://api/v1/')) {
          documentUrl = documentUrl.replace('https://api/v1/', `${API_URL}/`);
        } else if (documentUrl.startsWith('https://api/')) {
          const apiBase = API_URL.replace('/api/v1', '');
          documentUrl = documentUrl.replace('https://api/', `${apiBase}/api/`);
        }
        
        console.log('Downloading document with token:', documentUrl);
        
        // FIXED: Always use fetch with authorization token
        const response = await fetch(documentUrl, {
          method: 'GET',
          headers: {
            'authorization': 'Bearer ' + token
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        const openableTypes = ['pdf', 'image', 'text', 'video', 'audio'];
        const canOpen = openableTypes.some(type => 
          doc.mimetype?.toLowerCase().includes(type)
        );

        if (canOpen) {
          window.open(url, '_blank');
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
        return;
      }
      
      const res = await downloadDocument(token, doc.id);
      if (!res?.blob) return;
      
      const url = window.URL.createObjectURL(res.blob);
      
      const openableTypes = ['pdf', 'image', 'text', 'video', 'audio'];
      const canOpen = openableTypes.some(type => 
        doc.mimetype?.toLowerCase().includes(type)
      );

      if (canOpen) {
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to open document:', error);
      toast.error(`Failed to open document: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!token) return;

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deleteDocument(token, documentId);
      toast.success('Document deleted successfully');
      await loadData();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">Loading documents...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      
      {/* Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className="bg-white rounded-lg border border-gray-200 transition-shadow duration-200">
            {/* Header Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Title */}
                <div className="flex items-center space-x-6">
        <h1 className="text-2xl font-semibold">All Documents</h1>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {documents.length} documents
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
        <div className="flex items-center gap-4">
          <ValidateRole userRoles={['agency-admin']} expectedRoles={['agency-admin']}>
            <CreateDropdown onSelectProject={() => setShowNewProject(true)} />
          </ValidateRole>
          <NotificationButton />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content - aligned with header */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
      {documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <div className="text-center">
          <p className="text-lg font-medium">No documents found</p>
          <p className="text-sm">Documents uploaded to projects will appear here.</p>
              </div>
        </div>
      ) : (
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Table Header */}
              <div className="px-6 py-3 bg-gray-50" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Project</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Size</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Actions</span>
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="bg-white">
                {documents.map((doc) => (
                  <div key={doc.id} className="hover:bg-gray-50 transition-colors">
                    <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3 flex items-center">
                      <button
                        onClick={() => handleDocumentClick(doc)}
                            className="text-sm text-gray-900 hover:text-blue-600 underline-offset-2 hover:underline text-left truncate"
                      >
                        {doc.name}
                      </button>
                        </div>
                        <div className="col-span-2 flex items-center">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getProjectName(doc.projectId)}
                      </span>
                        </div>
                        <div className="col-span-1 flex items-center">
                          <span className="text-sm text-gray-500">{formatFileSize(doc.size)}</span>
                        </div>
                        <div className="col-span-2 flex items-center">
                          <span className="text-sm text-gray-500">{getFileType(doc.mimetype, doc.name)}</span>
                        </div>
                        <div className="col-span-2 flex items-center">
                          <span className="text-sm text-gray-500">{formatDate(doc.uploadedAt)}</span>
                        </div>
                        <div className="col-span-1 flex items-center">
                          <span className="text-sm text-gray-500 truncate">{doc.owner || doc.uploadedBy || '—'}</span>
                        </div>
                        <div className="col-span-1 flex items-center justify-end">
                      <button
                        onClick={() => handleDelete(doc.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default Documents; 